import { intlService } from '~/i18n/IntlService';
import { everest } from '~/services/serviceInstances';
import { toFile } from '~/utils/api';
import { sortByStringValue } from '~/utils/array';
import { cleanupWorkflow, mergeImplicitFields } from '~/utils/workflow';

// Backend-defined identifiers for built-in workflow fields (see
// mergeImplicitFields, which keys workflow.fields by these same strings).
// These are matching keys, not user-facing copy -- never run them through
// intl.formatMessage, or a 'field' entry stops resolving under any
// non-English locale (EKP-46775).
const IMPLICIT_FIELD_NAME = {
  WORKFLOW_NAME: 'Workflow Name',
  DATE_SIGNED: 'Date Signed',
  SUBMITTER_NAME: 'Submitter Name',
};

const createDefaultFilename = () => {
  const intl = intlService.getIntl();
  return [
    {
      type: 'field',
      value: IMPLICIT_FIELD_NAME.WORKFLOW_NAME,
    },
    {
      type: 'user-content',
      value: '_',
    },
    {
      type: 'user-content',
      value: intl.formatMessage({
        id: 'Workflows.insertCustomField',
        defaultMessage: 'Insert custom field or text',
        description: 'Placeholder text for custom field in filename',
      }),
    },
    {
      type: 'user-content',
      value: '_',
    },
    {
      type: 'field',
      value: IMPLICIT_FIELD_NAME.DATE_SIGNED,
    },
    {
      type: 'user-content',
      value: '_',
    },
    {
      type: 'field',
      value: IMPLICIT_FIELD_NAME.SUBMITTER_NAME,
    },
  ];
};

function toFormData(workflow) {
  const { file, ...payload } = workflow;
  const formData = new FormData();
  formData.append('payload', JSON.stringify(payload));
  if (file) {
    formData.append('file', file);
  }
  return formData;
}

export async function getAvailableWorkflows() {
  const workflows = await everest.get('/workflows/available/');
  return workflows
    .map((workflow) => ({
      ...workflow,
      isDraft: workflow.status === 'draft',
      isPublished: workflow.status === 'published',
      lastModifiedBy: workflow.lastModifiedById,
    }))
    .sort(sortByStringValue('name'));
}

export async function getWorkflow({ id, folderTreeId }) {
  const workflow = await everest.get(`/workflows/${id}/`);
  let {
    archiveLocation,
    ticketNamingConvention = null,
    keepSystemDefaultTicketName = true,
  } = workflow.settings;

  if (!archiveLocation) {
    archiveLocation = {
      pilotFolderId: null,
      filePath: [],
      fileName: [],
    };
  }

  if (!archiveLocation.pilotFolderId) {
    archiveLocation.pilotFolderId = folderTreeId;
  }

  if (!archiveLocation.fileName.length) {
    archiveLocation.fileName = createDefaultFilename();
  }

  const updatedSettings = {
    ...workflow.settings,
    ticketNamingConvention,
    keepSystemDefaultTicketName,
    archiveLocation,
  };

  return {
    ...mergeImplicitFields(workflow),
    settings: updatedSettings,
  };
}

export async function patchWorkflowSettings(workflow) {
  const cleanedWorkflow = cleanupWorkflow(workflow);
  return await everest.patch(
    `/workflows/${workflow.id}/settings`,
    cleanedWorkflow.settings,
  );
}

export async function createWorkflow(workflow) {
  const formData = toFormData(workflow);
  return await everest.post('/workflows/', formData);
}

export async function patchWorkflow(workflow) {
  const formData = toFormData(cleanupWorkflow(workflow));
  return await everest.patch(`/workflows/${workflow.id}`, formData);
}

export async function discardDraft({ id }) {
  return await everest.patch(`/workflows/${id}/discard-draft`);
}

export async function saveAndPublishWorkflow(workflow) {
  const formData = toFormData(cleanupWorkflow(workflow, true));
  return await everest.patch(`/workflows/${workflow.id}/publish`, formData);
}

export async function getWorkflowFile({ id, type = 'original' }) {
  const config = {
    fullResponse: true,
    responseType: 'blob',
  };
  const response = await everest.get(
    `/workflows/${id}/download/${type}`,
    config,
  );
  return toFile(response);
}

export async function getWorkflowIntakeForm(id) {
  return await everest.get(`/workflows/${id}/intake-form/link/`);
}

export async function parseTemplate(file) {
  const formData = toFormData({ file });
  return await everest.put('/workflows/parse-template', formData);
}
